/**
 * ds.js — acceso a los componentes del Yool DS desde las pantallas del cierre.
 *
 * CÓMO LLEGA EL DS A TADMIN
 * -------------------------
 * No por npm. `public/ds/_ds_bundle.js` es un IIFE que `src/index.js:26-38` carga
 * ANTES de montar React y que se auto-registra en
 * `window.DesignSystemRemix_1a4bd3`. El sufijo `_1a4bd3` es el prefijo del id del
 * proyecto de Claude Design: el bundle que corre en tadmin y el proyecto de
 * diseño son el mismo sistema.
 *
 * Ojo con instalar el paquete `apps-design-system/project/_dist` como dependencia:
 * su `_ds_bundle.js` es un build DISTINTO del que ya corre (md5 `49c7c967` contra
 * `00c0a0de`), así que registraría un segundo `window.DesignSystemRemix_1a4bd3`
 * pisando al primero. Se probó y se revirtió.
 *
 * POR QUÉ EL ACCESO ES LAZY
 * -------------------------
 * Se lee `window` en cada render, no al evaluar el módulo. Así el orden de
 * evaluación de los imports no importa: si este archivo se evaluara antes de que
 * el IIFE corriera, una captura al importar quedaría en `undefined` para siempre.
 * Es el mismo patrón que `components/product_config/formkit.jsx:27` y
 * `pages/SignIn.jsx:15`; aquí se extrae para no tenerlo una tercera vez.
 *
 * QUÉ PASA SI EL BUNDLE NO CARGÓ
 * ------------------------------
 * `src/index.js` monta la app igual si el script falla (tiene un `onerror`), así
 * que hay que sobrevivir a `DS()` vacío. Cada componente de aquí cae a un
 * equivalente tokenizado: mismo significado, sin depender del bundle. Lo que NO
 * se hace es renderizar `undefined` como componente — eso tira la pantalla
 * completa, y una pantalla de plata en blanco es peor que una fea.
 *
 * @see docs/design-system-unico.md — por qué el DS es el único sistema de tadmin
 * @see src/index.js — el cargador del bundle
 */
import React from 'react';

/** El namespace del bundle, leído en cada llamada. `{}` si todavía no cargó. */
export const DS = () => (typeof window !== 'undefined' && window.DesignSystemRemix_1a4bd3) || {};

/** ¿Está el bundle disponible? Para que una pantalla pueda avisar si no. */
export const dsListo = () => Object.keys(DS()).length > 0;

// ---------------------------------------------------------------------------
// Envoltorios con fallback
// ---------------------------------------------------------------------------
// Solo los componentes que el cierre usa. Cada uno con la firma del DS, para que
// migrar al paquete el día que exista sea borrar el fallback y nada más.

/**
 * Botón. `variant` primary | outline | ghost | destructive; `size` sm | md | lg.
 *
 * El fallback usa los mismos tokens que el DS, así que sin bundle se ve plano
 * pero correcto — y sobre todo sigue siendo un `<button>` con su `disabled`, que
 * es lo que importa en una pantalla donde un botón habilitado de más gira plata.
 */
export function Boton({ variant = 'primary', size = 'sm', arrow, children, ...rest }) {
  const { YoolButton } = DS();
  if (YoolButton) {
    return (
      <YoolButton variant={variant} size={size} arrow={arrow} {...rest}>
        {children}
      </YoolButton>
    );
  }
  const alto = size === 'lg' ? 48 : size === 'md' ? 40 : 32;
  const paleta =
    {
      primary: { background: 'var(--yool-cta)', color: '#fff', border: '1px solid var(--yool-cta)' },
      outline: {
        background: '#fff',
        color: 'var(--yool-black)',
        border: '1px solid var(--yool-border)',
      },
      ghost: { background: 'transparent', color: 'var(--yool-gray1)', border: '1px solid transparent' },
      destructive: {
        background: 'var(--yool-error)',
        color: '#fff',
        border: '1px solid var(--yool-error)',
      },
    }[variant] ?? {};
  return (
    <button
      type="button"
      {...rest}
      style={{
        ...paleta,
        height: alto,
        padding: '0 14px',
        borderRadius: 10,
        fontSize: 13,
        fontWeight: 600,
        cursor: rest.disabled ? 'not-allowed' : 'pointer',
        opacity: rest.disabled ? 0.5 : 1,
        fontFamily: 'inherit',
      }}
    >
      {children}
      {arrow ? ' →' : ''}
    </button>
  );
}

/** Badge semántico. `variant` de YoolBadge — nunca teal, nunca inventada. */
export function Badge({ variant = 'neutro', soft, ring, dot, children, ...rest }) {
  const { YoolBadge } = DS();
  if (YoolBadge) {
    return (
      <YoolBadge variant={variant} soft={soft} ring={ring} dot={dot} {...rest}>
        {children}
      </YoolBadge>
    );
  }
  return (
    <span
      {...rest}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        background: `var(--badge-${variant}-bg, var(--badge-sincob-bg))`,
        color: `var(--badge-${variant}-text, var(--badge-sincob-text))`,
        border: ring ? '1px solid currentColor' : '1px solid transparent',
        borderRadius: 20,
        padding: '2px 9px',
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

/**
 * Pill de estado del período. `estado` es una clave del mapa del DS
 * (`activo` | `procesando` | `pausado` | `error`…); el texto lo pone quien llama.
 *
 * NO ES `Badge`. El badge rotula una fila —una compañía, un día de la cola—; esto
 * rotula la PANTALLA, y por eso vive pegado al `<h1>`. El DS los dibuja distinto a
 * propósito: el pill lleva punto y radio completo.
 */
export function StatusPill({ estado = 'procesando', children, ...rest }) {
  const { StatusPill: Pill } = DS();
  if (Pill) {
    return (
      <Pill estado={estado} {...rest}>
        {children}
      </Pill>
    );
  }
  // El fallback mapea a tokens de badge, que sí son del DS: `procesando` es
  // información, `pausado` algo que esperar, `error` algo roto.
  const token =
    { activo: 'activo', procesando: 'digital', pausado: 'vence', error: 'vencido' }[estado]
    ?? 'sincob';
  return (
    <span
      {...rest}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: `var(--badge-${token}-bg)`,
        color: `var(--badge-${token}-text)`,
        borderRadius: 999,
        padding: '3px 10px',
        fontSize: 11.5,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

/**
 * KPI del período.
 *
 * `alert` pinta el valor en rojo y va SOLO cuando hay algo que atender: un KPI en
 * alerta permanente deja de comunicar.
 */
export function KPI({ label, value, context, icon, accent = 'slate', alert, ...rest }) {
  const { YoolKPI } = DS();
  if (YoolKPI) {
    return (
      <YoolKPI
        label={label}
        value={value}
        context={context}
        icon={icon}
        accent={accent}
        alert={alert}
        {...rest}
      />
    );
  }
  return (
    <div
      {...rest}
      style={{
        background: '#fff',
        border: '1px solid var(--yool-border)',
        borderRadius: 16,
        padding: '18px 20px',
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '.1em',
          textTransform: 'uppercase',
          color: 'var(--yool-muted)',
        }}
      >
        {label}
      </p>
      <p
        style={{
          margin: '10px 0 0',
          fontSize: 30,
          fontWeight: 700,
          letterSpacing: '-.03em',
          fontVariantNumeric: 'tabular-nums',
          color: alert ? 'var(--badge-vencido-text)' : 'var(--yool-black)',
        }}
      >
        {value}
      </p>
      {context ? (
        <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--yool-gray1)', lineHeight: 1.45 }}>
          {context}
        </p>
      ) : null}
    </div>
  );
}

/** Modal. `side="right"` lo vuelve panel lateral (la bitácora). */
export function Modal({
  open = true,
  onClose,
  eyebrow,
  title,
  desc,
  side,
  size = 'md',
  children,
  ...rest
}) {
  const { YoolModal } = DS();
  if (YoolModal) {
    return (
      <YoolModal
        open={open}
        onClose={onClose}
        eyebrow={eyebrow}
        title={title}
        desc={desc}
        side={side}
        size={size}
        {...rest}
      >
        {children}
      </YoolModal>
    );
  }
  if (!open) return null;
  const lateral = side === 'right';
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === 'string' ? title : undefined}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'var(--yool-overlay, rgba(0,0,0,.40))',
        display: 'flex',
        alignItems: lateral ? 'stretch' : 'center',
        justifyContent: lateral ? 'flex-end' : 'center',
        padding: lateral ? 0 : 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: lateral ? 0 : 20,
          padding: '24px 26px',
          width: lateral ? 460 : '100%',
          maxWidth: lateral ? 460 : size === 'lg' ? 760 : size === 'sm' ? 420 : 580,
          maxHeight: '100%',
          overflow: 'auto',
        }}
      >
        {eyebrow ? (
          <p
            style={{
              margin: 0,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '.13em',
              textTransform: 'uppercase',
              color: 'var(--yool-cta)',
            }}
          >
            {eyebrow}
          </p>
        ) : null}
        {title ? (
          <h2 style={{ margin: '7px 0 0', fontSize: 19, fontWeight: 700, letterSpacing: '-.02em' }}>
            {title}
          </h2>
        ) : null}
        {desc ? (
          <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--yool-gray1)', lineHeight: 1.55 }}>
            {desc}
          </p>
        ) : null}
        <div style={{ marginTop: 18 }}>{children}</div>
      </div>
    </div>
  );
}

/** Área de texto para las justificaciones. Nunca un `<textarea>` pelado. */
export function TextArea({ value, onChange, rows = 4, placeholder, ...rest }) {
  const { YoolTextarea } = DS();
  if (YoolTextarea) {
    return (
      <YoolTextarea
        value={value}
        onChange={onChange}
        rows={rows}
        placeholder={placeholder}
        {...rest}
      />
    );
  }
  return (
    <textarea
      value={value}
      onChange={onChange}
      rows={rows}
      placeholder={placeholder}
      {...rest}
      style={{
        width: '100%',
        padding: '10px 12px',
        border: '1px solid var(--yool-border)',
        borderRadius: 10,
        fontSize: 13,
        fontFamily: 'inherit',
        resize: 'vertical',
      }}
    />
  );
}

/** Input de una línea. Lo usa la palabra de confirmación. */
export function Input({ value, onChange, placeholder, ...rest }) {
  const { YoolInput } = DS();
  if (YoolInput) {
    return <YoolInput value={value} onChange={onChange} placeholder={placeholder} {...rest} />;
  }
  return (
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      {...rest}
      style={{
        width: '100%',
        height: 40,
        padding: '0 12px',
        border: '1px solid var(--yool-border)',
        borderRadius: 10,
        fontSize: 13,
        fontFamily: 'inherit',
      }}
    />
  );
}

/** Select. `options` acepta `{value,label}` o string. */
export function Select({ options = [], value, onChange, ...rest }) {
  const { YoolSelect } = DS();
  if (YoolSelect) return <YoolSelect options={options} value={value} onChange={onChange} {...rest} />;
  return (
    <select
      value={value}
      onChange={onChange}
      {...rest}
      style={{
        width: '100%',
        height: 40,
        padding: '0 10px',
        border: '1px solid var(--yool-border)',
        borderRadius: 10,
        fontSize: 13,
        fontFamily: 'inherit',
        background: '#fff',
      }}
    >
      {options.map((o) => {
        const v = typeof o === 'string' ? o : o.value;
        const l = typeof o === 'string' ? o : o.label;
        return (
          <option key={v} value={v}>
            {l}
          </option>
        );
      })}
    </select>
  );
}

/** Bitácora / audit trail. `items` con `{ id, title, subtitle, date, status }`. */
export function ActivityLog({ items = [], dot = true, ...rest }) {
  const { YoolActivityLog } = DS();
  if (YoolActivityLog) return <YoolActivityLog items={items} dot={dot} {...rest} />;
  return (
    <div {...rest} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {items.map((it, i) => (
        <div key={it.id ?? i} style={{ paddingBottom: 12, borderBottom: '1px solid var(--yool-gray)' }}>
          <div
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}
          >
            <span style={{ fontSize: 13.5, fontWeight: 600 }}>{it.title}</span>
            {it.status ? <Badge variant={it.status.variant ?? 'neutro'}>{it.status.label}</Badge> : null}
          </div>
          {it.subtitle ? (
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--yool-gray1)', lineHeight: 1.45 }}>
              {it.subtitle}
            </p>
          ) : null}
          {it.date ? (
            <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--yool-muted)' }}>{it.date}</p>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/**
 * Estado vacío.
 *
 * El copy lo decide quien llama, a propósito: distinguir "no hay nada" de "el
 * filtro no encontró nada" de "la llamada falló" es justamente lo que estas
 * pantallas tienen que comunicar bien, y un texto genérico lo borra.
 */
export function EmptyState({ icon = 'search', title, text, action, ...rest }) {
  const { YoolEmptyState } = DS();
  if (YoolEmptyState) {
    return <YoolEmptyState icon={icon} title={title} text={text} action={action} {...rest} />;
  }
  return (
    <div {...rest} style={{ padding: '44px 24px', textAlign: 'center' }}>
      <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{title}</p>
      {text ? (
        <p
          style={{
            margin: '7px auto 0',
            fontSize: 13,
            color: 'var(--yool-gray1)',
            lineHeight: 1.5,
            maxWidth: 480,
          }}
        >
          {text}
        </p>
      ) : null}
      {action ? <div style={{ marginTop: 14 }}>{action}</div> : null}
    </div>
  );
}
